/**
 * A gate node's condition nodes (Feature 79 D25, D26): one per branch in the
 * order tried, then the else when there is one. A condition node has no life
 * of its own on the wire: it is its gate's branch drawn as a node, and its id
 * `<gate>.<branch>` names it on the canvas and its position in the layout
 * (D32). No step or gate name holds a dot, so no id is ever both.
 */
import type { LinkageDecision } from '@optum/api-client/types';

/** The output an else takes; no branch may take its name. */
export const ELSE = 'else';
const SEPARATOR = '.';

export type ConditionKind = 'if' | 'else if' | 'else' | 'case' | 'default';

/** One condition node of a gate. */
export interface Condition {
  id: string;
  gate: string;
  /** Its branch's name; `else` for the else, and for a switch's default. */
  branch: string;
  kind: ConditionKind;
  /** Its place in the order tried; null on the else, which comes last. */
  number: number | null;
}

export function conditionId(gate: string, branch: string): string {
  return `${gate}${SEPARATOR}${branch}`;
}

/** The gate and branch an id names, or null when it is no condition node's. */
export function parseConditionId(id: string): { gate: string; branch: string } | null {
  const at = id.indexOf(SEPARATOR);
  if (at <= 0) return null;
  return { gate: id.slice(0, at), branch: id.slice(at + 1) };
}

/** A gate's condition nodes in the order tried, the else last. */
export function conditionsOf(decision: LinkageDecision): Condition[] {
  const switching = decision.mode === 'switch';
  const branches = decision.branches.map(
    (b, i): Condition => ({
      id: conditionId(decision.name, b.name),
      gate: decision.name,
      branch: b.name,
      kind: switching ? 'case' : i === 0 ? 'if' : 'else if',
      number: i + 1,
    }),
  );
  if (!decision.else) return branches;
  return [
    ...branches,
    {
      id: conditionId(decision.name, ELSE),
      gate: decision.name,
      branch: ELSE,
      kind: switching ? 'default' : 'else',
      number: null,
    },
  ];
}

/** The condition node an id names among the gates, with its gate, or null. */
export function findCondition(
  decisions: readonly LinkageDecision[],
  id: string,
): { decision: LinkageDecision; condition: Condition } | null {
  const parsed = parseConditionId(id);
  const decision = parsed && decisions.find((d) => d.name === parsed.gate);
  const condition = decision && conditionsOf(decision).find((c) => c.id === id);
  return decision && condition ? { decision, condition } : null;
}

/** A layout without the keys of a gate's condition nodes, or of one of them. */
export function withoutConditionKeys<T>(
  layout: Record<string, T>,
  gate: string,
  branch?: string,
): Record<string, T> {
  const kept: Record<string, T> = {};
  for (const [key, point] of Object.entries(layout)) {
    const parsed = parseConditionId(key);
    const gone = parsed?.gate === gate && (branch === undefined || parsed.branch === branch);
    if (!gone) kept[key] = point;
  }
  return kept;
}
